'use client';

import { useContext, useMemo, type CSSProperties } from 'react';
import { FullscreenTransformStateContext } from '@/app/contexts';

export const FULLSCREEN_ZOOM_STEP = 0.05;
export const FULLSCREEN_ZOOM_MAX_STEPS = 100;
export const FULLSCREEN_ZOOM_OUT_MAX_STEPS = 20;
export const FULLSCREEN_ZOOM_MIN_SCALE = 0.05;
/** Serenity ZoomControls presets (Components 986:467). */
export const FULLSCREEN_ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200] as const;

export function zoomLevelFromPercent(percent: number): number {
  return Math.round((percent / 100 - 1) / FULLSCREEN_ZOOM_STEP);
}

export function zoomPercentFromLevel(zoomLevel: number): number {
  return Math.round(getFullscreenZoomScale(zoomLevel) * 100);
}

/** Delay before applying crisp layout/raster zoom after the last zoom button click. */
export const FULLSCREEN_ZOOM_SETTLE_MS = 250;

export function getFullscreenZoomScale(zoomLevel: number): number {
  return Math.max(FULLSCREEN_ZOOM_MIN_SCALE, 1 + zoomLevel * FULLSCREEN_ZOOM_STEP);
}

/**
 * Rotation applied while painting an image / compact still.
 *
 * Compact stills (`showCompactCaptureStill`) and capture preview paint from
 * props before `displayItem` is resolved, so callers must pass
 * `suppressRotation` rather than reading `displayItem.mediaContext`.
 */
export function resolveImagePaintRotation(args: {
  suppressRotation: boolean;
  temporaryRotation?: number;
  persistedRotation?: number;
}): { persistedRotation: number | undefined; temporaryRotation: number | undefined } {
  if (args.suppressRotation) {
    return { persistedRotation: 0, temporaryRotation: 0 };
  }
  return {
    persistedRotation: args.persistedRotation,
    temporaryRotation: args.temporaryRotation,
  };
}

/**
 * Rotate is a no-op on a saved capture still (already rasterized at its final
 * orientation). Hide the buttons in Capture view rather than leaving them
 * clickable or disabled-and-inert (TR-3020). Keep them mounted on Full Media
 * even when the current type cannot rotate, so the toolbar does not reflow
 * when a capture exists or can be created.
 */
export function shouldShowMediaRotateControls(args: {
  isTitleSlide: boolean;
  viewingCapture: boolean;
  isRotatable: boolean;
  captureEligible: boolean;
}): boolean {
  if (args.isTitleSlide || args.viewingCapture) return false;
  return args.isRotatable || args.captureEligible;
}

/** Normalize image rotation for a CSS `rotate()` so negative values wrap. */
export function imagePaintRotationDeg(
  temporaryRotation?: number,
  persistedRotation?: number,
): number {
  return (((temporaryRotation ?? persistedRotation ?? 0) % 360) + 360) % 360;
}

export function getFullscreenZoomCssScaleBoost(previewLevel: number, settledLevel: number): number {
  const settledScale = getFullscreenZoomScale(settledLevel);
  if (settledScale <= 0) {
    return 1;
  }
  return getFullscreenZoomScale(previewLevel) / settledScale;
}

/**
 * Scroll-canvas size for content laid out at the settled raster size while a CSS
 * ``cssScaleBoost`` preview is applied. Keeps layout size equal to the visible
 * (preview) size so settle (boost → 1, raster grows) does not jump.
 * Returns 0 when ``settledPx`` is not yet known so callers can fall back to 100%.
 */
export function getFullscreenZoomVisualSize(settledPx: number, cssScaleBoost: number): number {
  if (settledPx <= 0) {
    return 0;
  }
  return Math.max(1, Math.round(settledPx * cssScaleBoost));
}

/**
 * After zoom/layout changes, clamp scroll into range. Do not re-center — that
 * made overflowed media feel anchored to the bottom-right; leaving scroll at
 * the origin (and growing the canvas from the top-left) matches PdfViewer.
 */
export function preserveFullscreenScrollTopLeft(
  el: HTMLElement,
  contentWidth: number,
  contentHeight: number,
): void {
  const maxLeft = Math.max(0, contentWidth - el.clientWidth);
  const maxTop = Math.max(0, contentHeight - el.clientHeight);
  if (el.scrollLeft > maxLeft) {
    el.scrollLeft = maxLeft;
  }
  if (el.scrollTop > maxTop) {
    el.scrollTop = maxTop;
  }
}

/**
 * Keep the horizontal focal point when the scroll canvas width changes.
 * Vertical is top-anchored: content grows downward so ``scrollTop`` is left alone
 * (``scrollTop === 0`` always shows the top of the page under the controls).
 */
export function preserveFullscreenScrollTopAnchor(
  el: HTMLElement,
  prevContentWidth: number,
  nextContentWidth: number,
): void {
  if (prevContentWidth <= 0 || nextContentWidth <= 0 || prevContentWidth === nextContentWidth) {
    return;
  }
  const centerX = el.scrollLeft + el.clientWidth / 2;
  el.scrollLeft = Math.max(0, (centerX / prevContentWidth) * nextContentWidth - el.clientWidth / 2);
}

/**
 * Keep the same content point under the viewport center when the scroll canvas
 * grows/shrinks (crossing the "fits in view" threshold during zoom).
 */
export function preserveFullscreenScrollCenter(
  el: HTMLElement,
  prevContentWidth: number,
  prevContentHeight: number,
  nextContentWidth: number,
  nextContentHeight: number,
): void {
  if (
    prevContentWidth <= 0 ||
    prevContentHeight <= 0 ||
    nextContentWidth <= 0 ||
    nextContentHeight <= 0
  ) {
    return;
  }
  if (prevContentWidth === nextContentWidth && prevContentHeight === nextContentHeight) {
    return;
  }
  const centerX = el.scrollLeft + el.clientWidth / 2;
  const centerY = el.scrollTop + el.clientHeight / 2;
  el.scrollLeft = Math.max(0, (centerX / prevContentWidth) * nextContentWidth - el.clientWidth / 2);
  el.scrollTop = Math.max(0, (centerY / prevContentHeight) * nextContentHeight - el.clientHeight / 2);
}

export function getFullscreenMinZoomLevel(zoomUiEnabled: boolean, isZoomable: boolean): number {
  if (zoomUiEnabled && isZoomable) {
    return -FULLSCREEN_ZOOM_OUT_MAX_STEPS;
  }
  return 0;
}

export type DeferredFullscreenZoom = {
  previewLevel: number;
  layoutLevel: number;
  previewScale: number;
  layoutScale: number;
  cssScaleBoost: number;
  isZoomPending: boolean;
};

export function useDeferredFullscreenZoom(
  isFullscreen: boolean,
  localZoomLevel = 0,
): DeferredFullscreenZoom {
  const { zoomLevel: previewLevel, settledZoomLevel } = useContext(FullscreenTransformStateContext);

  return useMemo(() => {
    if (!isFullscreen) {
      const scale = getFullscreenZoomScale(localZoomLevel);
      return {
        previewLevel: localZoomLevel,
        layoutLevel: localZoomLevel,
        previewScale: scale,
        layoutScale: scale,
        cssScaleBoost: 1,
        isZoomPending: false,
      };
    }
    const previewScale = getFullscreenZoomScale(previewLevel);
    const layoutScale = getFullscreenZoomScale(settledZoomLevel);
    const cssScaleBoost = getFullscreenZoomCssScaleBoost(previewLevel, settledZoomLevel);
    return {
      previewLevel,
      layoutLevel: settledZoomLevel,
      previewScale,
      layoutScale,
      cssScaleBoost,
      isZoomPending: previewLevel !== settledZoomLevel,
    };
  }, [isFullscreen, localZoomLevel, previewLevel, settledZoomLevel]);
}

/** Inverse-scale wrapper so CSS preview zoom does not distort scroll container layout. */
export function fullscreenZoomPreviewWrapperStyle(cssScaleBoost: number): CSSProperties | undefined {
  if (cssScaleBoost === 1) {
    return undefined;
  }
  const inv = 1 / cssScaleBoost;
  return {
    transform: `scale(${cssScaleBoost})`,
    transformOrigin: '0 0',
    width: `${inv * 100}%`,
    height: `${inv * 100}%`,
    willChange: 'transform',
  };
}

export function appendCssScaleTransform(existingTransform: string, cssScaleBoost: number): string {
  if (cssScaleBoost === 1) {
    return existingTransform;
  }
  return `${existingTransform} scale(${cssScaleBoost})`;
}

type ZoomPreviewApplier = (previewLevel: number, settledLevel: number) => void;

let zoomPreviewApplier: ZoomPreviewApplier | null = null;

/** Register a viewport hook for synchronous CSS preview before React re-renders. */
export function registerFullscreenZoomPreviewApplier(applier: ZoomPreviewApplier | null) {
  zoomPreviewApplier = applier;
}

/** Paint an immediate CSS zoom preview (no layout / raster work). */
export function applyImmediateFullscreenZoomPreview(previewLevel: number, settledLevel: number) {
  zoomPreviewApplier?.(previewLevel, settledLevel);
}
